import {
  visibleClients as policyVisibleClients,
  visibleFirms as policyVisibleFirms,
  visibleProjects as policyVisibleProjects,
  visibleWorkPackages as policyVisibleWorkPackages,
  withheldWorkPackageCount as policyWithheldWorkPackageCount,
  type AccessContext,
} from '@/access/roleAccess'
import type { RoleKey } from '@/access/roles'
import {
  ANCHOR_FIRM_ID,
  ASSIGNMENTS,
  CLIENTS,
  FIRMS,
  PROJECTS,
  WORK_PACKAGES,
  personById,
  roleByKey,
  type Client,
  type Firm,
  type Project,
  type WorkPackage,
} from '@/demo/data'

/**
 * The demo seed's {@link AccessContext}, and the seed-bound scoping helpers the
 * badged concept surfaces call.
 *
 * PX-W6.6 (TASK-188E6E40, spec §6.3 item 6) moved this consumption OUT of
 * `src/access/roleAccess.ts`: access policy must be pure over whatever entities
 * it is handed, never over a fixture. The policy functions are unchanged in
 * meaning — this module only supplies their context and re-exports them under
 * their original names, so the concept surfaces read exactly as before.
 *
 * There is no live firm / work-package domain to build a context from yet; when
 * one arrives (the W4 build board, the super-admin cross-firm backend), it
 * assembles its own context and calls the SAME policy functions.
 */

export type DemoAccessContext = AccessContext<Firm, Client, Project, WorkPackage>

/**
 * The seed's answer to "who is this principal", derived from the role's own
 * `RoleDef` bindings. A firm-side role resolves an office and its assignments;
 * a client-side role resolves the client (and, for `client-employee`, the one
 * project).
 */
export const demoAccessContext = (role: RoleKey): DemoAccessContext => {
  const definition = roleByKey(role)
  const { personId = null, clientId = null, projectId = null } = definition
  return {
    anchorFirmId: ANCHOR_FIRM_ID,
    firms: FIRMS,
    clients: CLIENTS,
    projects: PROJECTS,
    workPackages: WORK_PACKAGES,
    principal: {
      office: personId === null ? null : personById(personId).office,
      assignedProjectIds:
        personId === null
          ? []
          : ASSIGNMENTS.filter((a) => a.personId === personId).map((a) => a.projectId),
      clientId,
      projectId,
    },
  }
}

export const visibleFirms = (role: RoleKey): readonly Firm[] =>
  policyVisibleFirms(role, demoAccessContext(role))

export const visibleClients = (role: RoleKey): readonly Client[] =>
  policyVisibleClients(role, demoAccessContext(role))

export const visibleProjects = (role: RoleKey): readonly Project[] =>
  policyVisibleProjects(role, demoAccessContext(role))

export const visibleWorkPackages = (
  role: RoleKey,
  projectId?: string
): readonly WorkPackage[] =>
  policyVisibleWorkPackages(role, demoAccessContext(role), projectId)

export const withheldWorkPackageCount = (role: RoleKey, projectId?: string): number =>
  policyWithheldWorkPackageCount(role, demoAccessContext(role), projectId)
